import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { api, invalidateData, useMeta } from '../../api.ts';
import { optionalCount } from '../../../shared/schemas.ts';
import { ErrorBox, Help, Loading, Spinner, useToast } from '../../components/ui.tsx';
import { useUserContext } from './UserLayout.tsx';
import { useFixed } from './FixedOhcwPage.tsx';

const key = (band: number) => `b${Math.round(band * 100)}`;
type Values = Record<string, string>;

export function FixedMarathonPage() {
  const { user } = useUserContext();
  const meta = useMeta();
  const fixed = useFixed(user.id);
  const toast = useToast();
  const { register, handleSubmit, reset, setError, formState } = useForm<Values>();
  const bands = meta.data?.bands ?? [];

  useEffect(() => {
    if (!fixed.data) return;
    reset(Object.fromEntries(fixed.data.marathon.map((r) => [key(r.band), String(r.value)])));
  }, [fixed.data, reset]);

  const save = useMutation({
    mutationFn: (body: unknown) => api.put(`/users/${encodeURIComponent(user.id)}/fixed/marathon`, body),
    onSuccess: () => {
      invalidateData();
      toast.ok('Marathon-arvot tallennettu');
    },
    onError: toast.error,
  });

  const onSubmit = (v: Values) => {
    let invalid = false;
    const values = bands.map((b) => {
      const res = optionalCount.safeParse(v[key(b.band)]);
      if (!res.success) {
        invalid = true;
        setError(key(b.band), { message: res.error.issues[0]?.message });
      }
      return { band: b.band, value: res.success ? res.data : null };
    });
    if (!invalid) save.mutate({ values });
  };

  if (meta.isLoading || fixed.isLoading) return <Loading />;
  if (fixed.error) return <ErrorBox error={fixed.error} />;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="stack" noValidate>
      <div className="row">
        <p className="muted" style={{ margin: 0, flex: 1 }}>
          Bandikohtaiset maamäärät OH Marathon -taulukkoon. Suurempi arvo (syötetty tai QSO:ista laskettu) näytetään.{' '}
          <strong>0 (nolla)</strong> piilottaa sinut kyseiseltä bandilta.
        </p>
        <Help name="OmatMarathon" />
      </div>
      <div className="table-wrap">
        <table className="table compact">
          <thead>
            <tr>
              {bands.map((b) => (
                <th key={b.band} className="num">
                  {b.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {bands.map((b) => (
                <td key={b.band} className="num">
                  <input
                    className="input input-sm input-num"
                    inputMode="numeric"
                    aria-label={`${b.label} m`}
                    aria-invalid={!!formState.errors[key(b.band)]}
                    {...register(key(b.band))}
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {Object.keys(formState.errors).length > 0 && <div className="alert alert-error">Arvojen on oltava kokonaislukuja 0–1000.</div>}
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={save.isPending}>
          {save.isPending ? <Spinner /> : 'Tallenna'}
        </button>
      </div>
    </form>
  );
}
